import { useEffect, useState } from 'react';

import { obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { formatearImporte } from '../../../nucleo/importes/formatoDeImporte.js';
import { consultarPanel } from '../../vencimientos/api/vencimientosApi.js';
import { aNumero, porcentajeEnCobro } from '../logica/deudaDescompuesta.js';
import '../../../estilos/deuda-descompuesta.css';

/**
 * LA DEUDA DESCOMPUESTA EN EL DETALLE DE LA TARJETA (incremento 18/21, RF-252,
 * RN-699, RN-700; prototipo aprobado v39, pantalla `-tarjeta-detalle`).
 *
 * TRES COSAS QUE ESTA PANTALLA TIENE QUE DEJAR CLARAS, Y EN ESTE ORDEN:
 *
 *   1. LA DEUDA TOTAL NO CAMBIA AL SEPARARLA (RN-699). Es lo primero que se dice,
 *      porque es la duda que aparece al ver dos cifras donde antes había una.
 *      Separar REPARTE la deuda inicial; no la reduce ni un centavo.
 *
 *   2. CÓMO ESTÁ REPARTIDA la deuda inicial: en cobro y en compras a meses, con la
 *      suma a la vista. El exigible inicial se DERIVA y no se guarda (RN-687).
 *
 *   3. EN QUÉ MOMENTO ESTÁ lo declarado frente a los planes registrados —los TRES
 *      momentos que dibuja el prototipo—:
 *        · declarado SIN planes todavía → es el estado real entre esta entrega y
 *          la siguiente, y se dice en pantalla (RN-700);
 *        · TRASLAPE → la misma compra estaría descontada dos veces; se REPORTA,
 *          nunca se ajusta (D-383, D-389);
 *        · CUADRADO → ya hay planes detrás y lo declarado es lo que falta.
 *      Los textos de los momentos los arma el SERVIDOR (RNF-130) y aquí se pintan.
 *
 * DE DÓNDE SALEN LAS CIFRAS: la deuda inicial y el diferido declarado vienen con
 * la cuenta; el diferido respaldado por planes y el pago esperado vienen del panel
 * de vencimientos, consultado para ESTA tarjeta con el parámetro que ya existe en
 * su contrato. NO SE INVENTA NINGUNA OPERACIÓN NUEVA y esta pantalla no calcula
 * ninguna de las dos cifras derivadas.
 *
 * SI EL PANEL NO RESPONDE, el bloque del reparto se sigue viendo entero —solo
 * necesita la cuenta— y el de los momentos no se dibuja: ninguna cifra se inventa
 * y nada se pinta a medias.
 */
export function DeudaDescompuestaDeLaTarjeta({ cuenta }) {
  const hoy = obtenerFechaDeNegocioHoy();
  const [renglon, setRenglon] = useState(null);

  /*
   * RN-752 (incremento 20/21) · LISTA EXPLÍCITA DE TIPO, nunca `esCredito`. La deuda
   * descompuesta del 18 es un universo DE TARJETAS —tiene corte y diferido— y el
   * préstamo queda fuera por tipo: su fórmula RN-692 no participa, y su renglón del
   * panel se deriva contra el pago del mes (D-438).
   */
  const esTarjetaDeCredito = cuenta?.claveTipoCuenta === 'CREDITO';

  useEffect(() => {
    if (!esTarjetaDeCredito) return undefined;

    let vigente = true;
    consultarPanel({ fechaLocal: hoy, idCuenta: cuenta.idCuenta, incluirSinSaldo: true })
      .then((panel) => {
        if (!vigente) return;
        setRenglon((panel?.tarjetas ?? []).find((t) => t.idCuenta === cuenta.idCuenta) ?? null);
      })
      .catch(() => {
        // Informativo: si no se puede, el reparto de arriba se sigue viendo y
        // ninguna cifra se inventa.
        if (vigente) setRenglon(null);
      });

    return () => { vigente = false; };
  }, [cuenta, hoy, esTarjetaDeCredito]);

  if (!esTarjetaDeCredito) return null;

  const iso = cuenta.codigoISO;
  const deudaInicial = aNumero(cuenta.saldoInicial);
  const declarado = aNumero(cuenta.diferidoDeclarado);
  const enCobro = deudaInicial - declarado;
  const porcentaje = porcentajeEnCobro(cuenta.saldoInicial, cuenta.diferidoDeclarado);

  return (
    <>
      {/* 1 · LA CIFRA DE ARRIBA NO SE MUEVE (RN-699, CA-18-04). */}
      <div className="aviso ok" data-prueba="detalle-total-no-cambia">
        <b>Esta cifra no cambia al separar tu deuda.</b> Separar sirve para saber qué te cobran
        ahora y qué después. Lo que debes en total es lo mismo antes y después, al centavo.
      </div>

      {/* 2 · CÓMO ESTÁ REPARTIDA LA DEUDA INICIAL (RF-252, RN-687). */}
      <div className="card lista" data-prueba="detalle-desglose">
        <h3>La deuda con la que empezó esta tarjeta</h3>

        <div className="fila">
          <span className="crece">Deuda con la que empezó</span>
          <b data-prueba="detalle-deuda-inicial">{formatearImporte(cuenta.saldoInicial, iso)}</b>
        </div>
        <div className="fila">
          <span className="crece">
            De eso, en cobro
            <small>lo que el banco ya te cobra</small>
          </span>
          <b data-prueba="detalle-en-cobro">{formatearImporte(enCobro, iso)}</b>
        </div>
        <div className="fila">
          <span className="crece">
            De eso, en compras a meses
            <small>lo declaraste tú</small>
          </span>
          <b data-prueba="detalle-diferido-declarado">{formatearImporte(cuenta.diferidoDeclarado, iso)}</b>
        </div>
        <div className="fila">
          <span className="crece"><b>Suma</b></span>
          <b data-prueba="detalle-suma">{formatearImporte(cuenta.saldoInicial, iso)}</b>
        </div>

        {/* La barra es apoyo visual y NO es el portador del significado: las tres
            cifras ya están escritas arriba y la leyenda las nombra. */}
        <div className="barra-de-reparto" data-prueba="detalle-barra" aria-hidden="true">
          <i className="en-cobro" style={{ width: `${porcentaje}%` }} />
          <i className="a-meses" style={{ width: `${100 - porcentaje}%` }} />
        </div>
        <span className="ayuda" data-prueba="detalle-leyenda-del-reparto">
          El tramo oscuro es lo que está <b>en cobro</b> y el claro, lo que está{' '}
          <b>en compras a meses que aún no te cobran</b>.
        </span>
      </div>

      {/* 3 · LOS TRES MOMENTOS. El texto lo arma el servidor (RNF-130): declarado
          sin planes, traslape o ya cuadrado. */}
      {renglon?.textoAvisoDeDiferidoDeclarado && (
        <div className="aviso w" data-prueba="detalle-estado-del-respaldo">
          {renglon.textoAvisoDeDiferidoDeclarado}
        </div>
      )}

      {renglon && declarado > 0 && !renglon.textoAvisoDeDiferidoDeclarado && (
        <div className="aviso ok" data-prueba="detalle-estado-cuadrado">
          <b>Lo que declaraste y tus planes ya cuadran.</b> De tus compras a meses,{' '}
          {formatearImporte(renglon.importeDiferidoPorPlanes, iso)} están respaldadas por un plan
          registrado y quedan {formatearImporte(cuenta.diferidoDeclarado, iso)} declarados a mano.
        </div>
      )}

      {renglon?.textoAvisoDeDiferidoFueraDeRango && (
        <div className="aviso w" data-prueba="detalle-diferido-fuera-de-rango">
          {renglon.textoAvisoDeDiferidoFueraDeRango}
        </div>
      )}

      {/* ESTE PERIODO · lo que se espera que pagues, con su desglose ya armado por
          el servidor. Es la misma cifra del panel: si las dos pantallas enseñaran
          números distintos, sería defecto. */}
      {renglon?.importePagoEsperado && (
        <div className="card lista" data-prueba="detalle-periodo">
          <h3>Este periodo</h3>
          <div className="fila">
            <span className="crece">Lo que se espera que pagues</span>
            <b data-prueba="detalle-pago-esperado">
              {formatearImporte(renglon.importePagoEsperado, iso)}
            </b>
          </div>
          <div className="fila">
            <span className="crece">Lo que llevas pagado de este periodo</span>
            <b data-prueba="detalle-cubierto">{formatearImporte(renglon.importeCubierto, iso)}</b>
          </div>
          <span className="ayuda" data-prueba="detalle-estado-deducido" data-clave-estado={renglon.claveEstado}>
            {renglon.textoEstado}
          </span>
          <span className="ayuda">{renglon.textoDesgloseDePagoEsperado}</span>
        </div>
      )}
    </>
  );
}
